import ResponderAlertModal from 'features/Responder/components/ResponderAlertModal';
import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';

/**
 * PersonnelOptions
 *
 * Renders a Bootstrap button labelled with the person's name. Clicking it
 * opens a Modal containing assignment checkboxes:
 *   • "All Occurrences"  — assign to every occurrence of the schedule
 *   • One entry per scopedOption — assign to a subset of occurrences
 *   • "Specific Days of Week" — when checked, shows a weekday button row;
 *     each button independently toggles that day's weekly recurring assignment
 */
export const PersonnelOptions = ({
  person,
  mode,
  thisOccurrenceRRule = null,
  onModeSelect,
  weekdayOptions = [],
  activeWeekdayRrules = new Set(),
  onWeekdayToggle,
  onClearWeekdays,
}) => {
  const [show, setShow] = useState(false);
  // Local open/close state for the day-picker section, kept in sync with active rrules.
  const [weekdayPickerOpen, setWeekdayPickerOpen] = useState(activeWeekdayRrules.size > 0);

  // Close the weekday picker whenever a mutually exclusive mode becomes active.
  useEffect(() => {
    if (mode === 'this' || mode === 'all') setWeekdayPickerOpen(false);
  }, [mode]);

  // Consider the user assigned if they have any active mode or weekday assignments.
  const isAssigned = mode !== null || activeWeekdayRrules.size > 0;

  // Generate the label summarizing this person's current assignment mode(s).
  const modeLabel =
    activeWeekdayRrules.size > 0
      ? weekdayOptions
          .filter((o) => activeWeekdayRrules.has(o.rrule))
          .map((o) => o.short)
          .join(', ')
      : mode === null
        ? 'Not assigned'
        : mode === 'all'
          ? 'All Occurrences'
          : mode === 'this'
            ? 'This Occurrence Only'
            : 'Assigned';

  const handleWeekdaySectionToggle = () => {
    if (weekdayPickerOpen) {
      // Uncheck → clear all active weekday assignments and close picker
      onClearWeekdays?.(person.userId);
      setWeekdayPickerOpen(false);
    } else {
      // Opening the weekday picker: deselect any mutually exclusive modes.
      if (mode === 'this') {
        onModeSelect(person.userId, 'this');
      }
      if (mode === 'all') {
        onModeSelect(person.userId, 'all');
      }
      setWeekdayPickerOpen(true);
    }
  };

  return (
    <>
      <Button
        variant={isAssigned ? 'primary' : 'outline'}
        style={{
          backgroundColor: isAssigned ? '#325277' : 'transparent',
        }}
        size="sm"
        className="d-flex flex-column align-items-start"
        onClick={() => setShow(true)}
      >
        <div className="fw-bold">{person.name}</div>
        <span>{modeLabel}</span>
      </Button>

      <ResponderAlertModal
        show={show}
        title={person.name}
        onCancel={() => setShow(false)}
        showFooter={false}
      >
        <div className="d-flex flex-column gap-2">
          {/* Assign to All */}
          <label
            className="d-flex align-items-center gap-2 mb-0 user-select-none"
            style={{ cursor: 'pointer' }}
          >
            <input
              type="checkbox"
              checked={mode === 'all'}
              onChange={() => onModeSelect(person.userId, 'all')}
            />
            <span
              style={{
                color: mode === 'all' ? '#4ea1ff' : 'rgba(255,255,255,0.55)',
              }}
            >
              All Occurrences
            </span>
          </label>

          {/* This Occurrence Only */}
          {thisOccurrenceRRule && (
            <label
              className="d-flex align-items-center gap-2 mb-0 user-select-none"
              style={{ cursor: 'pointer' }}
            >
              <input
                type="checkbox"
                checked={mode === 'this'}
                onChange={() => onModeSelect(person.userId, 'this')}
              />
              <span
                style={{
                  color: mode === 'this' ? '#4ea1ff' : 'rgba(255,255,255,0.55)',
                }}
              >
                This Occurrence Only
              </span>
            </label>
          )}

          {/* Specific Days of Week */}
          {weekdayOptions.length > 0 && (
            <div className="d-flex flex-column gap-2 mt-1">
              <label
                className="d-flex align-items-center gap-2 mb-0 user-select-none"
                style={{ cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={weekdayPickerOpen}
                  onChange={handleWeekdaySectionToggle}
                />
                <span
                  style={{
                    color: weekdayPickerOpen ? '#4ea1ff' : 'rgba(255,255,255,0.55)',
                  }}
                >
                  Specific Days of Week
                </span>
              </label>
              {weekdayPickerOpen && (
                <div className="d-flex flex-wrap gap-1 ps-4">
                  {weekdayOptions.map((opt) => {
                    const active = activeWeekdayRrules.has(opt.rrule);
                    return (
                      <Button
                        key={opt.jsDay}
                        size="sm"
                        variant={active ? 'primary' : 'outline-secondary'}
                        onClick={() => onWeekdayToggle?.(person.userId, opt.rrule)}
                      >
                        {opt.short}
                      </Button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </ResponderAlertModal>
    </>
  );
};
